// useFilePreview (E7.2.1 → estendido em E7.2.2) — busca do dispatcher de preview.
//
// Híbrido lazy (decisão congelada E7.2.1): só dispara quando o usuário abre o
// preview (`enabled: open`). A resposta traz uma `url` assinada FRESCA + o
// `render_hint` validado por magic-bytes.
//
// E7.2.2 adiciona o LOOP DE POLLING de Office (status `pending` → `ready`):
// enquanto o dispatcher devolve `pending`, re-busca a cada `poll_after_seconds`
// até um teto (`FILE_PREVIEW_MAX_POLLS`). O backend NUNCA persiste `failed`
// (decisão frozen E7.1.3: worker em falha não grava row → dispatcher segue em
// `pending`), então o "falhou" é decidido AQUI: estourado o teto ⇒ `pollExhausted`.
// O 413 do dispatcher (Office > 50MB) vira `isTooLarge` (terminal, sem retry).

import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import filePreviewService from "@/services/filePreviewService";
import type { FilePreviewResponse } from "@/types/filePreview";

/**
 * Teto de buscas de um Office em `pending` (inclui a carga inicial). Até 24
 * buscas espaçadas por `poll_after_seconds` (~5s default do dispatcher) ≈ 2min —
 * folga sobre os ~30s esperados de conversão. Estourado ⇒ a UI declara falha
 * (`pollExhausted`).
 */
export const FILE_PREVIEW_MAX_POLLS = 24;

/** Intervalo de fallback se o dispatcher não mandar `poll_after_seconds`. */
const DEFAULT_POLL_SECONDS = 5;

/** True se o erro é uma resposta HTTP com o status dado (shape do AxiosError). */
function isHttpStatus(error: unknown, code: number): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === code;
}

export interface UseFilePreviewResult {
  data: FilePreviewResponse | undefined;
  /** Primeira busca em voo (sem dado ainda). */
  isLoading: boolean;
  /** Erro genérico (404/415/503/network) — distinto de `isTooLarge`. */
  isError: boolean;
  /** Office ainda convertendo (`pending`) e dentro do teto de polls. */
  isPolling: boolean;
  /** Polling estourou o teto sem ficar `ready` — "falhou" do ponto de vista da UI. */
  pollExhausted: boolean;
  /** Dispatcher devolveu 413 (Office > 50MB). Terminal: não adianta repetir. */
  isTooLarge: boolean;
  /** Reinicia o preview do zero (zera o contador de polls). Botão "Tentar novamente". */
  retry: () => void;
}

export function useFilePreview(
  storageKey: string | null,
  options?: { enabled?: boolean }
): UseFilePreviewResult {
  // `attempt` no queryKey: bumpá-lo cria uma entrada de cache nova ⇒
  // `dataUpdateCount` zera ⇒ o polling recomeça do zero. (`refetch()` preservaria
  // o contador e o cap nunca reiniciaria.)
  const [attempt, setAttempt] = useState(0);
  const queryClient = useQueryClient();
  const queryKey = ["file-preview", storageKey, attempt] as const;
  const enabled = !!storageKey && (options?.enabled ?? true);

  const query = useQuery<FilePreviewResponse>({
    queryKey,
    queryFn: () => filePreviewService.getPreview(storageKey as string),
    enabled,
    staleTime: 60_000,
    // gcTime 0: ao fechar o dialog (query sem observers) a entrada é descartada,
    // então REABRIR sempre começa fresco. Sem isso, reabrir um Office que já
    // falhou (pollExhausted) reusaria o `dataUpdateCount` estourado do cache e
    // ficaria preso em "falhou" até o GC (5min) — e cada retry vazaria entradas.
    gcTime: 0,
    refetchOnWindowFocus: false,
    // CRÍTICO p/ o cap: cada poll devolve `pending` idêntico, então por default o
    // RQ não notificaria o observer (data não muda) e o componente não
    // re-renderizaria ao atingir o teto — ficaria preso em "convertendo". `all`
    // força re-render a cada mudança de estado (incl. `dataUpdateCount`).
    notifyOnChangeProps: "all",
    // 413 (Office > 50MB) é terminal — não retenta. Demais: 1 retry rápido.
    retry: (failureCount, error) => !isHttpStatus(error, 413) && failureCount < 1,
    // Polling: re-busca enquanto `pending` e dentro do teto; senão para.
    refetchInterval: (q) => {
      const d = q.state.data;
      if (d?.status !== "pending") return false; // inline/ready/erro → para
      if (q.state.dataUpdateCount >= FILE_PREVIEW_MAX_POLLS) return false; // cap
      // Guard: 0/negativo (ou ausente) → fallback. Intervalo 0 faria o RQ parar
      // o polling (spinner travaria). O backend garante ge=1, mas defendemos.
      const secs =
        d.poll_after_seconds && d.poll_after_seconds > 0
          ? d.poll_after_seconds
          : DEFAULT_POLL_SECONDS;
      return secs * 1000;
    },
  });

  // `dataUpdateCount` não é exposto no resultado do useQuery — lemos do cache.
  // É reativo: cada poll resolve uma busca → re-render → leitura fresca aqui.
  const updateCount =
    queryClient.getQueryState<FilePreviewResponse>(queryKey)?.dataUpdateCount ?? 0;

  // `enabled` gate: dialog fechado não está "pollando", mesmo com `pending` no cache.
  const isPending = enabled && query.data?.status === "pending";
  const pollExhausted = isPending && updateCount >= FILE_PREVIEW_MAX_POLLS;
  const isPolling = isPending && !pollExhausted;
  const isTooLarge = isHttpStatus(query.error, 413);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  return {
    data: query.data,
    isLoading: query.isLoading,
    // 413 tem corpo próprio (`isTooLarge`); não duplica no erro genérico.
    isError: query.isError && !isTooLarge,
    isPolling,
    pollExhausted,
    isTooLarge,
    retry,
  };
}

export default useFilePreview;
